// =============================================================================
// TickAndPass Shared Constants
// =============================================================================
// Values that both frontend and backend must agree on.
// Anything that lives here must NOT depend on Node.js-only or browser-only APIs.
// =============================================================================

// -----------------------------------------------------------------------------
// User types and roles
// -----------------------------------------------------------------------------
export const UserType = Object.freeze({
  PROFESSIONAL: "PROFESSIONAL",
  INSTITUTION_OFFICER: "INSTITUTION_OFFICER",
  ORGANISATION_OFFICER: "ORGANISATION_OFFICER",
  PLATFORM_ADMIN: "PLATFORM_ADMIN",
});

// Mirrors the OfficerRole enum in packages/backend/prisma/schema.prisma.
export const OfficerRole = Object.freeze({
  PRIMARY_ADMIN: "PRIMARY_ADMIN", // Registered the institution or organisation
  ADMIN: "ADMIN",
  VERIFICATION_OFFICER: "VERIFICATION_OFFICER",
  MEMBER: "MEMBER",
});

// -----------------------------------------------------------------------------
// Passwords
// -----------------------------------------------------------------------------
// At least 8 characters, with a lowercase letter, an uppercase letter and a
// number. The server enforces the same rule in its auth DTOs
// (packages/backend/src/auth/dto); clients check it only for early feedback.
export const PASSWORD_COMPLEXITY = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

// -----------------------------------------------------------------------------
// Identity numbers, as the server's verification DTOs check them
// (packages/backend/src/identity-verification/dto)
// -----------------------------------------------------------------------------
export const AADHAAR_PATTERN = /^[2-9]\d{11}$/;
export const GSTIN_PATTERN = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[\dA-Z]$/;
export const PAN_PATTERN = /^[A-Z]{5}\d{4}[A-Z]$/;
export const OTP_PATTERN = /^\d{6}$/;

export const AssignmentPolicy = Object.freeze({
  SHARED_POOL: "SHARED_POOL", // Default: any officer can act on any pending request
  EXPLICIT_ASSIGNMENT: "EXPLICIT_ASSIGNMENT", // Admin assigns each request to a specific officer
});

export const UserStatus = Object.freeze({
  ACTIVE: "ACTIVE",
  PENDING_VERIFICATION: "PENDING_VERIFICATION",
  SUSPENDED: "SUSPENDED",
  DELETED: "DELETED",
});

export const OrgStatus = Object.freeze({
  PENDING_REVIEW: "PENDING_REVIEW",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  SUSPENDED: "SUSPENDED",
});

// -----------------------------------------------------------------------------
// Credentials
// -----------------------------------------------------------------------------
export const CredentialType = Object.freeze({
  EDUCATION: "EDUCATION",
  WORK: "WORK",
  OTHER: "OTHER",
});

export const VerificationStatus = Object.freeze({
  NONE: "NONE",
  PENDING: "PENDING",
  VERIFIED: "VERIFIED",
  REJECTED: "REJECTED",
  // A request only: closed undecided because its fee was refunded.
  WITHDRAWN: "WITHDRAWN",
});

export const VerifierType = Object.freeze({
  INSTITUTION: "INSTITUTION",
  ORGANISATION: "ORGANISATION",
});

// -----------------------------------------------------------------------------
// Payments - prices in PAISE (1 rupee = 100 paise)
// -----------------------------------------------------------------------------
// Source of truth is the .env file; these are fallbacks if env is not loaded.
// NEVER hardcode different values in frontend and backend.
export const DEFAULT_PRICES_PAISE = Object.freeze({
  PROFESSIONAL_MEMBERSHIP: 18000, // Rs. 180 / year
  ORGANISATION_SUBSCRIPTION: 1200000, // Rs. 12,000 / year
  INSTITUTION_SUBSCRIPTION: 1200000, // Rs. 12,000 / year
  VERIFICATION_FEE: 50000, // Rs. 500 / verification
});

export const DEFAULT_VERIFICATION_REVENUE_SHARE_PERCENT = 25;

export const PaymentType = Object.freeze({
  PROFESSIONAL_MEMBERSHIP: "PROFESSIONAL_MEMBERSHIP",
  INSTITUTION_SUBSCRIPTION: "INSTITUTION_SUBSCRIPTION",
  ORGANISATION_SUBSCRIPTION: "ORGANISATION_SUBSCRIPTION",
  VERIFICATION_FEE: "VERIFICATION_FEE",
  ADDITIONAL_OFFICER_FEE: "ADDITIONAL_OFFICER_FEE",
});

export const PaymentStatus = Object.freeze({
  CREATED: "CREATED",
  AUTHORIZED: "AUTHORIZED",
  CAPTURED: "CAPTURED",
  FAILED: "FAILED",
  REFUND_PENDING: "REFUND_PENDING",
  REFUNDED: "REFUNDED",
  // Activated by a 100% discount coupon; nothing was charged.
  COUPON_REDEEMED: "COUPON_REDEEMED",
});

export const LedgerEntryType = Object.freeze({
  PLATFORM_REVENUE: "PLATFORM_REVENUE",
  PAYABLE_TO_INSTITUTION: "PAYABLE_TO_INSTITUTION",
  PAYABLE_TO_ORGANISATION: "PAYABLE_TO_ORGANISATION",
  REFUND: "REFUND",
});

// -----------------------------------------------------------------------------
// Notifications
// -----------------------------------------------------------------------------
export const NotificationType = Object.freeze({
  VERIFICATION_APPROVED: "VERIFICATION_APPROVED",
  VERIFICATION_REJECTED: "VERIFICATION_REJECTED",
  VERIFICATION_REQUEST_RECEIVED: "VERIFICATION_REQUEST_RECEIVED",
  ORG_APPLICATION_APPROVED: "ORG_APPLICATION_APPROVED",
  ORG_APPLICATION_REJECTED: "ORG_APPLICATION_REJECTED",
  CANDIDATE_PROFILE_REQUEST: "CANDIDATE_PROFILE_REQUEST",
  PAYMENT_RECEIVED: "PAYMENT_RECEIVED",
  PAYMENT_FAILED: "PAYMENT_FAILED",
  MEMBERSHIP_EXPIRING: "MEMBERSHIP_EXPIRING",
  OFFICER_INVITE: "OFFICER_INVITE",
  ACCOUNT_SUSPENDED: "ACCOUNT_SUSPENDED",
  GENERIC: "GENERIC",
  REQUEST_MESSAGE: "REQUEST_MESSAGE",
  EMPLOYMENT_ENDED: "EMPLOYMENT_ENDED",
  PROFILE_VIEWED: "PROFILE_VIEWED",
  JOIN_REQUEST_RECEIVED: "JOIN_REQUEST_RECEIVED",
  JOB_APPLICATION_RECEIVED: "JOB_APPLICATION_RECEIVED",
});

// -----------------------------------------------------------------------------
// File upload limits
// -----------------------------------------------------------------------------
export const MAX_DOCUMENT_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
export const ALLOWED_DOCUMENT_MIME_TYPES = Object.freeze([
  "application/pdf",
  "image/jpeg",
  "image/png",
]);
export const ALLOWED_DOCUMENT_EXTENSIONS = Object.freeze([
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
]);

// -----------------------------------------------------------------------------
// API paths (kept in one place so renaming is a single edit)
// -----------------------------------------------------------------------------
export const ApiPath = Object.freeze({
  // ---------------------------------------------------------------------------
  // Auth & users
  // ---------------------------------------------------------------------------
  ME: "/api/me", // GET own user
  REGISTER_PROFESSIONAL: "/api/auth/register/professional", // POST
  REGISTER_INSTITUTION: "/api/auth/register/institution", // POST
  REGISTER_ORGANISATION: "/api/auth/register/organisation", // POST

  // Sensitive personal info changes (require OTP re-verification)
  CHANGE_EMAIL: "/api/me/email", // POST initiates, PATCH confirms
  CHANGE_PHONE: "/api/me/phone", // POST initiates, PATCH confirms

  // ---------------------------------------------------------------------------
  // Professional
  // ---------------------------------------------------------------------------
  PROFESSIONAL_PROFILE: "/api/professional/profile", // GET, PATCH
  PROFESSIONAL_AVATAR: "/api/professional/avatar", // PUT (upload), DELETE (remove)
  PROFESSIONAL_SHARE_TOGGLE: "/api/professional/share/toggle", // POST - turn share link on/off
  PROFESSIONAL_SHARE_REGENERATE: "/api/professional/share/regenerate", // POST - new token

  PROFESSIONAL_CREDENTIALS: "/api/professional/credentials", // GET (list), POST (create)
  PROFESSIONAL_CREDENTIAL: (id) => `/api/professional/credentials/${id}`, // GET, PATCH, DELETE
  PROFESSIONAL_CREDENTIAL_REQUEST_EDIT: (
    id, // POST - paid edit request
  ) => `/api/professional/credentials/${id}/request-edit`, //        for verified credentials
  PROFESSIONAL_CREDENTIAL_REUPLOAD: (
    id, // POST - replace certificate file
  ) => `/api/professional/credentials/${id}/reupload`, //        only for unverified credentials

  PROFESSIONAL_VERIFICATIONS: "/api/professional/verifications", // GET
  REQUEST_VERIFICATION: "/api/professional/request-verification", // POST
  PROFESSIONAL_HISTORY: "/api/professional/history", // GET

  // ---------------------------------------------------------------------------
  // Academic Institution
  // ---------------------------------------------------------------------------
  INSTITUTION_DASHBOARD: "/api/institution/dashboard", // GET
  INSTITUTION_PROFILE: "/api/institution/profile", // GET, PATCH
  INSTITUTION_REUPLOAD_DOCUMENT: "/api/institution/registration-document", // PUT - re-upload registration doc

  INSTITUTION_REQUESTS: "/api/institution/requests", // GET (incl. edit-context requests)
  INSTITUTION_VERIFY: (id) => `/api/institution/requests/${id}/verify`, // POST
  INSTITUTION_REJECT: (id) => `/api/institution/requests/${id}/reject`, // POST
  INSTITUTION_ASSIGN_REQUEST: (
    id, // POST - admin assigns request to specific officer
  ) => `/api/institution/requests/${id}/assign`,
  INSTITUTION_UNASSIGN_REQUEST: (
    id, // POST - admin returns request to the shared pool
  ) => `/api/institution/requests/${id}/unassign`,
  INSTITUTION_ASSIGNMENT_POLICY: "/api/institution/assignment-policy", // GET, PATCH

  INSTITUTION_OFFICERS: "/api/institution/officers", // GET (list), POST (invite new)
  INSTITUTION_OFFICER: (id) => `/api/institution/officers/${id}`, // GET, PATCH, DELETE
  INSTITUTION_OFFICER_DISABLE: (
    id, // POST - soft-disable
  ) => `/api/institution/officers/${id}/disable`,
  INSTITUTION_OFFICER_ENABLE: (
    id, // POST - re-enable
  ) => `/api/institution/officers/${id}/enable`,
  INSTITUTION_OFFICER_PROMOTE: (
    id, // POST - promote to ADMIN role
  ) => `/api/institution/officers/${id}/promote`,
  INSTITUTION_TRANSFER_ADMIN: "/api/institution/transfer-admin", // POST - hand over admin role

  INSTITUTION_VERIFIED_STUDENTS: "/api/institution/students", // GET

  // ---------------------------------------------------------------------------
  // Organisation
  // ---------------------------------------------------------------------------
  ORG_DASHBOARD: "/api/organisation/dashboard", // GET
  ORG_PROFILE: "/api/organisation/profile", // GET, PATCH
  ORG_REUPLOAD_DOCUMENT: "/api/organisation/registration-document", // PUT

  ORG_EMP_REQUESTS: "/api/organisation/employee-requests", // GET
  ORG_VERIFY_EMP: (id) => `/api/organisation/employee-requests/${id}/verify`,
  ORG_REJECT_EMP: (id) => `/api/organisation/employee-requests/${id}/reject`,
  ORG_ASSIGN_REQUEST: (
    id, // POST - admin assigns request
  ) => `/api/organisation/employee-requests/${id}/assign`,
  ORG_UNASSIGN_REQUEST: (
    id, // POST - admin returns to pool
  ) => `/api/organisation/employee-requests/${id}/unassign`,
  ORG_ASSIGNMENT_POLICY: "/api/organisation/assignment-policy", // GET, PATCH

  ORG_CANDIDATE_REQUESTS: "/api/organisation/candidate-requests", // GET, POST
  ORG_CANDIDATE_REQUEST: (id) => `/api/organisation/candidate-requests/${id}`, // GET, DELETE (cancel)

  ORG_OFFICERS: "/api/organisation/officers", // GET, POST
  ORG_OFFICER: (id) => `/api/organisation/officers/${id}`, // GET, PATCH, DELETE
  ORG_OFFICER_DISABLE: (id) => `/api/organisation/officers/${id}/disable`, // POST
  ORG_OFFICER_ENABLE: (id) => `/api/organisation/officers/${id}/enable`, // POST
  ORG_OFFICER_PROMOTE: (id) => `/api/organisation/officers/${id}/promote`, // POST
  ORG_TRANSFER_ADMIN: "/api/organisation/transfer-admin", // POST

  // ---------------------------------------------------------------------------
  // Payments
  // ---------------------------------------------------------------------------
  PAYMENT_CREATE_ORDER: "/api/payments/create-order", // POST
  PAYMENT_VERIFY: "/api/payments/verify", // POST (after Razorpay returns)
  PAYMENT_WEBHOOK: "/api/payments/webhook", // POST (Razorpay calls this)

  // ---------------------------------------------------------------------------
  // Storage
  // ---------------------------------------------------------------------------
  UPLOAD_URL: "/api/storage/upload-url", // POST - returns presigned PUT URL
  DOWNLOAD_URL: (key) =>
    `/api/storage/download-url?key=${encodeURIComponent(key)}`,

  // ---------------------------------------------------------------------------
  // Platform admin
  // ---------------------------------------------------------------------------
  ADMIN_PENDING_ORGS: "/api/admin/pending-organisations", // GET
  ADMIN_APPROVE_ORG: (id) => `/api/admin/organisations/${id}/approve`, // POST
  ADMIN_REJECT_ORG: (id) => `/api/admin/organisations/${id}/reject`, // POST
  ADMIN_REREVIEW_ORG: (id) => `/api/admin/organisations/${id}/re-review`, // POST - reopen rejected app

  ADMIN_EDIT_ORG: (id) => `/api/admin/organisations/${id}`, // PATCH - admin can fix typos
  ADMIN_EDIT_INSTITUTION: (id) => `/api/admin/institutions/${id}`, // PATCH

  ADMIN_STATS: "/api/admin/stats", // GET
  ADMIN_SUSPEND_USER: (id) => `/api/admin/users/${id}/suspend`, // POST
  ADMIN_UNSUSPEND_USER: (id) => `/api/admin/users/${id}/unsuspend`, // POST
  ADMIN_AUDIT_LOG: "/api/admin/audit-log", // GET
  ADMIN_REFUND: (paymentId) => `/api/admin/refunds/${paymentId}`, // POST

  // Platform settings (prices, commission %, system messages)
  ADMIN_SETTINGS: "/api/admin/settings", // GET, PATCH

  // Notifications fed to admin when events need attention
  ADMIN_NOTIFICATIONS: "/api/admin/notifications", // GET (list), PATCH (mark read)

  // ---------------------------------------------------------------------------
  // Public (no auth)
  // ---------------------------------------------------------------------------
  PUBLIC_SHARED_PROFILE: (token) => `/api/public/profiles/${token}`, // GET
});
